import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Loader2, UserRound, Lock } from 'lucide-react'
import { useAuth } from '../hooks/use-auth'

const loginSchema = z.object({
  identifier: z.string().min(1, 'Champ requis'),
  password: z.string().min(1),
  rememberMe: z.boolean(),
})

type LoginFormValues = z.infer<typeof loginSchema>

export function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login, error, clearError } = useAuth()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { rememberMe: true },
  })

  const onSubmit = async (values: LoginFormValues) => {
    clearError()
    try {
      const isEmail = values.identifier.includes('@')
      const credentials = isEmail
        ? { email: values.identifier, password: values.password, rememberMe: values.rememberMe }
        : { login: values.identifier, password: values.password, rememberMe: values.rememberMe }
      await login(credentials)
      navigate('/dashboard', { replace: true })
    } catch {
      // Error is handled by AuthContext
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-neutral-900">
          {t('auth.login.title')}
        </h1>
        <p className="text-sm text-neutral-500">
          Connectez-vous pour accéder à votre espace
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {error && (
          <div className="rounded-lg bg-error/10 border border-error/20 px-4 py-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* Identifier (email ou login) */}
        <div className="space-y-1.5">
          <label htmlFor="identifier" className="block text-sm font-medium text-neutral-700">
            {t('auth.login.email')}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
              <UserRound className="h-4 w-4" />
            </span>
            <input
              id="identifier"
              type="text"
              autoComplete="username"
              placeholder="email ou identifiant"
              className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 pl-10 pr-4 text-sm transition-all placeholder:text-neutral-400 hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:opacity-50"
              {...register('identifier')}
            />
          </div>
          {errors.identifier && (
            <p className="text-xs text-error">{errors.identifier.message}</p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
            {t('auth.login.password')}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
              <Lock className="h-4 w-4" />
            </span>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 pl-10 pr-10 text-sm transition-all placeholder:text-neutral-400 hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:opacity-50"
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
              aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-error">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me + Forgot password */}
        <div className="flex items-center justify-between">
          <label htmlFor="rememberMe" className="flex items-center gap-2 cursor-pointer select-none">
            <input
              id="rememberMe"
              type="checkbox"
              defaultChecked
              className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary/25"
              {...register('rememberMe')}
            />
            <span className="text-sm text-neutral-600">{t('auth.login.rememberMe')}</span>
          </label>
          <Link to="/forgot-password" className="text-sm text-primary hover:text-primary-800 transition-colors">
            {t('auth.login.forgotPassword')}
          </Link>
        </div>

        {/* Submit — gradient + strong shadow */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-gradient-to-r from-primary to-primary-800 px-6 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:from-primary-800 hover:to-primary-950 hover:shadow-xl hover:shadow-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('auth.login.submitting')}
            </>
          ) : (
            t('auth.login.submit')
          )}
        </button>

      </form>
    </div>
  )
}
